import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { CriticalPointTemplate } from "src/app/classes/model/practice-path";
import {
  CriticalPointTemplateService,
  CreateCriticalPointTemplatePayload,
  UpdateCriticalPointTemplatePayload,
} from "../../services/critical-point-template.service";

export type CriticalPointTemplateEditDialogMode = "create" | "edit";

export interface CriticalPointTemplateEditDialogData {
  mode: CriticalPointTemplateEditDialogMode;
  template?: CriticalPointTemplate;
}

@Component({
  selector: "app-critical-point-template-edit-dialog",
  templateUrl: "./critical-point-template-edit-dialog.component.html",
  styleUrls: ["./critical-point-template-edit-dialog.component.scss"],
})
export class CriticalPointTemplateEditDialogComponent implements OnInit {
  protected dialogTitle: string;
  protected submitButtonText: string;
  protected templateForm: FormGroup;
  protected isSaving = false;

  constructor(
    private readonly dialogRef: MatDialogRef<CriticalPointTemplateEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: CriticalPointTemplateEditDialogData,
    private readonly formBuilder: FormBuilder,
    private readonly templateService: CriticalPointTemplateService
  ) {}

  ngOnInit(): void {
    this.dialogTitle =
      this.data.mode === "create"
        ? "Új kritikus pont sablon"
        : "Kritikus pont sablon szerkesztése";
    this.submitButtonText = this.data.mode === "create" ? "Létrehozás" : "Mentés";

    this.templateForm = this.formBuilder.group({
      title: [
        this.data.template?.title ?? "",
        [Validators.required, Validators.minLength(3), Validators.maxLength(200)],
      ],
      description: [
        this.data.template?.description ?? "",
        [Validators.required, Validators.minLength(10), Validators.maxLength(1000)],
      ],
      isGlobal: [this.data.template?.isGlobal ?? true],
    });
  }

  protected async onSubmit(): Promise<void> {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    try {
      const title = this.templateForm.get("title")?.value?.trim() ?? "";
      const description = this.templateForm.get("description")?.value?.trim() ?? "";
      const isGlobal = this.templateForm.get("isGlobal")?.value ?? true;

      let result: CriticalPointTemplate | undefined;

      if (this.data.mode === "create") {
        const payload: CreateCriticalPointTemplatePayload = {
          title,
          description,
          isGlobal,
        };
        result = await this.templateService.createTemplate(payload);
      } else {
        const payload: UpdateCriticalPointTemplatePayload = {
          title,
          description,
          isGlobal,
        };
        result = await this.templateService.updateTemplate(
          this.data.template!.id,
          payload
        );
      }

      if (result) {
        this.dialogRef.close(result);
      } else {
        this.isSaving = false;
      }
    } catch (error) {
      this.isSaving = false;
    }
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  protected onLocalToggle(event: MatCheckboxChange): void {
    const isLocal = event.checked;
    const control = this.templateForm.get("isGlobal");
    if (!control) {
      return;
    }

    control.setValue(!isLocal);
    control.markAsDirty();
    control.markAsTouched();
  }

  protected getErrorMessage(fieldName: string): string {
    const control = this.templateForm.get(fieldName);
    if (!control || !control.errors || !control.touched) {
      return "";
    }

    if (control.errors["required"]) {
      return "Ez a mező kötelező";
    }
    if (control.errors["minlength"]) {
      const minLength = control.errors["minlength"].requiredLength;
      return `Minimum ${minLength} karakter szükséges`;
    }
    if (control.errors["maxlength"]) {
      const maxLength = control.errors["maxlength"].requiredLength;
      return `Maximum ${maxLength} karakter engedélyezett`;
    }

    return "";
  }
}

